import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * The catalog's numbered pagination, the only way between its pages.
 *
 * Plain links in the server-rendered HTML, and that is the point of them. From
 * 19 Sep to 2 Oct 2026 the only way past page one was Load more, a button no
 * crawler can press, so pages 2 onwards were linked from nowhere and reachable
 * only through the sitemap. Every page now links to the first, the last and
 * the two either side of it, so following Next alone reaches every page.
 *
 * prefetch is off on every link. A row of up to nine page links, each one a
 * dynamic render of 120 names, is a lot to fetch for a visitor who may click
 * none of them.
 */

/** Page numbers to show: the first, the last, and the current page plus or minus two. */
export function pageWindow(current: number, total: number): Array<number | 'gap'> {
  const wanted = new Set<number>([1, total]);
  for (let p = current - 2; p <= current + 2; p += 1) {
    if (p >= 1 && p <= total) wanted.add(p);
  }
  const sorted = [...wanted].sort((a, b) => a - b);
  const out: Array<number | 'gap'> = [];
  let prev = 0;
  for (const p of sorted) {
    // A gap of exactly one page shows that page: "1 2 3" reads better than
    // "1 ... 3" and costs no more room than the dots would.
    if (p - prev === 2) out.push(p - 1);
    else if (p - prev > 2) out.push('gap');
    out.push(p);
    prev = p;
  }
  return out;
}

const PILL =
  'tabular inline-flex h-9 min-w-9 items-center justify-center rounded-full px-3 text-sm font-semibold';
const LINK_PILL = `${PILL} border border-[var(--border)] bg-[var(--surface)] text-[var(--text-secondary)] transition-colors hover:border-[var(--accent)] hover:text-[var(--accent)]`;

interface CatalogPaginationProps {
  page: number;
  totalPages: number;
  /** The URL of a page of the current view, so a search keeps its query. */
  hrefFor: (page: number) => string;
}

export function CatalogPagination({ page, totalPages, hrefFor }: CatalogPaginationProps) {
  if (totalPages <= 1) return null;
  return (
    <nav aria-label="Catalog pages" className="mt-6 flex flex-wrap items-center justify-center gap-1.5">
      {page > 1 ? (
        <Link
          href={hrefFor(page - 1)}
          prefetch={false}
          rel="prev"
          aria-label="Previous page"
          className={`${LINK_PILL} gap-1`}
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          <span className="hidden sm:inline">Previous</span>
        </Link>
      ) : null}

      {pageWindow(page, totalPages).map((item, i) =>
        item === 'gap' ? (
          <span
            key={`gap-${i}`}
            aria-hidden="true"
            className="inline-flex h-9 min-w-6 items-center justify-center text-sm text-[var(--text-muted)]"
          >
            &hellip;
          </span>
        ) : item === page ? (
          <span
            key={item}
            aria-current="page"
            className={`${PILL} bg-[var(--text-secondary)] text-white`}
          >
            {item}
          </span>
        ) : (
          <Link
            key={item}
            href={hrefFor(item)}
            prefetch={false}
            aria-label={`Page ${item}`}
            className={LINK_PILL}
          >
            {item}
          </Link>
        ),
      )}

      {page < totalPages ? (
        <Link
          href={hrefFor(page + 1)}
          prefetch={false}
          rel="next"
          aria-label="Next page"
          className={`${LINK_PILL} gap-1`}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="size-4" aria-hidden="true" />
        </Link>
      ) : null}
    </nav>
  );
}
